<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'wasteBinReadings',
    path: '/waste-bin-readings',
    component: 'WasteBinReadings',
    title: () => gettext('Waste Bin Readings'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'WasteBinReadings',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const selectedItems = ref([]);
  const dsParams = ref({
    include_worksheets: false,
  });
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
    resizable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'timestamp',
      name: $fn.value.pgettext('WasteBinReadings', 'Reading Time'),
      type: 'command',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'datetime',
          },
          {
            name: 'link',
            href: '#',
            className: 'command-link',
          },
        ],
      },
    },
    {
      field: 'waste_bin_name',
      name: $fn.value.pgettext('WasteBinReadings', 'Bin Name'),
      formatter: {
        name: 'routeLink',
        to: 'fixedAsset',
        params: {
          id: 'waste_bin_id',
        },
      },
    },
    {
      field: 'waste_bin_number',
      name: $fn.value.pgettext('WasteBinReadings', 'Bin Number'),
    },
    {
      field: 'waste_bin_capacity',
      name: $fn.value.pgettext('WasteBinReadings', 'Capacity (liters)'),
    },
    {
      field: 'state',
      name: $fn.value.pgettext('WasteBinReadings', 'State'),
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'gettext',
            context: 'WasteBinReadingStates',
            conversion: 'StartCase',
          },
          {
            name: 'badge',
            variantmap: {
              empty: 'success',
              full: 'warning',
            },
            variantMapSource: 'fieldValue',
          },
        ],
      },
    },
    {
      field: 'rental_name',
      name: $fn.value.pgettext('WasteBinReadings', 'Rental'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'rental',
        params: {
          id: 'rental_id',
        },
      },
    },
    {
      field: 'client_number',
      name: $fn.value.pgettext('WasteBinReadings', 'Client Number'),
      formatter: {
        name: 'routeLink',
        to: 'company',
        params: {
          id: 'client_id',
        },
      },
    },
    {
      field: 'client_name',
      name: $fn.value.pgettext('WasteBinReadings', 'Client Name'),
      formatter: {
        name: 'routeLink',
        to: 'company',
        params: {
          id: 'client_id',
        },
      },
    },
    {
      field: 'cycle',
      name: $fn.value.pgettext('WasteBinReadings', 'Cycle'),
    },
    {
      field: 'device_id',
      name: $fn.value.pgettext('WasteBinReadings', 'Device ID'),
    },
    {
      field: 'worksheets',
      name: $fn.value.pgettext('WasteBinReadings', 'Worksheets'),
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        separator: ', ',
        formatterContext: 'item',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'number',
              source: 'value',
            },
          },
          {
            name: 'routeLink',
            to: 'worksheet',
            params: {
              id: 'id',
            },
          },
        ],
      },
    },
  ]);
  const b_2248226175642056_contextMenu = computed(() => ({
    items: [
      {
        label: $fn.value.gettext('Delete'),
        command: 'remove',
      },
    ],
  }));

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    dsParams: dsParams.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
    b_2248226175642056_contextMenu: b_2248226175642056_contextMenu.value,
  }));

  const $$load_ds = async () => {
    return $fn.value.slickDataSource('list_waste_bin_readings', {
      parameters: dsParams.value,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      {
        name: 'dsParams',
        cache: { type: 'local', key: 'WasteBinReadings.dsParams' },
      },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_contextMenu', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      selectedItems,
      dsParams,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
      b_2248226175642056_contextMenu,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.gettext('Waste Bin Readings'),
      icon: $config.value.wasteBin.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_qezm0oqnm_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_2herory17_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_reloadSlickgrid_w3fzp5y4y_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'wasteBinReadings',
    };
  };
  const act_2248226175642056_request_kzlrphggl_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'waste_bins/delete_reading',
      data: { identities: [$event.data.data.id] },
    };
  };
  const act_2248226175642056_showModal_bgeo72quz_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'WasteBinReadingModal',
      props: {
        objectId: $event.data.data.id,
      },
    };
  };
  const inst_evh_2248226175642056_command_dvvl4if8n_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'timestamp';
  };
  const act_2095089031469157_showModal_d2ncis1dx_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateWorksheetsFromWasteBinReadingsModal',
      props: {
        readings: _.map(selectedItems.value, (p) => p.id),
      },
    };
  };
  const act_9004123373732307_reloadSlickgrid_0fndcufod_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'wasteBinReadings',
    };
  };
  const act_9004123373732307_request_fey5qac38_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'waste_bins/delete_readings',
      data: { identities: selectedItems.value.map((p) => p.id) },
    };
  };
  const act_9004123373732307_request_fey5qac38_getConfirm = async ($event) => {
    return {
      header: '_("Delete the selected readings?")',
      message: $fn.value.gettext('This action cannot be undone.'),
      btnOk: $fn.value.gettext('Delete'),
    };
  };

  const act_7315092382398562_closeModal_qezm0oqnm =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_qezm0oqnm_getActionArgs,
    });
  const inst_evh_7315092382398562_close_1gxo2p8r9 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_qezm0oqnm,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_1gxo2p8r9],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_yq6h8ovi3 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_cxgrkfjjo =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_yq6h8ovi3,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_cxgrkfjjo],
    event: 'reload',
  });
  const act_2248226175642056_setData_2herory17 = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_2herory17_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_49vknzxsy =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_2herory17,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_49vknzxsy],
      event: 'selectedRowsChanged',
    });
  const act_2248226175642056_reloadSlickgrid_w3fzp5y4y =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_2248226175642056_reloadSlickgrid_w3fzp5y4y_getActionArgs,
    });
  const inst_evh_2248226175642056_success_pgwb7aopw =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_reloadSlickgrid_w3fzp5y4y,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_2248226175642056_request_kzlrphggl = new actionTypes.RequestAction({
    actionArgs: act_2248226175642056_request_kzlrphggl_getActionArgs,
    events: [inst_evh_2248226175642056_success_pgwb7aopw],
  });
  const inst_evh_2248226175642056_contextmenuRemove_m0hdwr84e =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_request_kzlrphggl,
      event: 'contextmenu:remove',
      displayName: 'request',
    });
  const inst_evh_2248226175642056_contextmenuRemove =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_contextmenuRemove_m0hdwr84e],
      event: 'contextmenu:remove',
    });
  const act_2248226175642056_showModal_bgeo72quz =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_bgeo72quz_getActionArgs,
      displayName: 'showWasteBinReadingModal',
    });
  const inst_evh_2248226175642056_command_dvvl4if8n =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_bgeo72quz,
      event: 'command',
      when: inst_evh_2248226175642056_command_dvvl4if8n_getWhen,
      displayName: 'showWasteBinReadingModal',
    });
  const inst_evh_2248226175642056_command = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2248226175642056_command_dvvl4if8n],
    event: 'command',
  });
  const act_2095089031469157_showModal_d2ncis1dx =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_d2ncis1dx_getActionArgs,
    });
  const inst_evh_2095089031469157_click_rjyb5j3fi =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_d2ncis1dx,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2095089031469157_click_rjyb5j3fi],
    event: 'click',
  });
  const act_9004123373732307_reloadSlickgrid_0fndcufod =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_9004123373732307_reloadSlickgrid_0fndcufod_getActionArgs,
    });
  const inst_evh_9004123373732307_success_0wh7g6rst =
    new actionTypes.EventHandlerImpl({
      action: act_9004123373732307_reloadSlickgrid_0fndcufod,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_9004123373732307_request_fey5qac38 = new actionTypes.RequestAction({
    actionArgs: act_9004123373732307_request_fey5qac38_getActionArgs,
    confirm: act_9004123373732307_request_fey5qac38_getConfirm,
    displayName: 'deleteItems',
    events: [inst_evh_9004123373732307_success_0wh7g6rst],
  });
  const inst_evh_9004123373732307_click_jk0fhmzg6 =
    new actionTypes.EventHandlerImpl({
      action: act_9004123373732307_request_fey5qac38,
      event: 'click',
      displayName: 'deleteItems',
    });
  const inst_evh_9004123373732307_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_9004123373732307_click_jk0fhmzg6],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_close_1gxo2p8r9,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_cxgrkfjjo,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_49vknzxsy,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2248226175642056_success_pgwb7aopw,
    inst_evh_2248226175642056_contextmenuRemove_m0hdwr84e,
    inst_evh_2248226175642056_contextmenuRemove,
    inst_evh_2248226175642056_command_dvvl4if8n,
    showWasteBinReadingModal: inst_evh_2248226175642056_command_dvvl4if8n,
    inst_evh_2248226175642056_command,
    inst_evh_2095089031469157_click_rjyb5j3fi,
    inst_evh_2095089031469157_click,
    inst_evh_9004123373732307_success_0wh7g6rst,
    inst_evh_9004123373732307_click_jk0fhmzg6,
    deleteItems: inst_evh_9004123373732307_click_jk0fhmzg6,
    inst_evh_9004123373732307_click,
  };

  const evh_7315092382398562_close_1gxo2p8r9 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_1gxo2p8r9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_cxgrkfjjo = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_cxgrkfjjo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_49vknzxsy = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_49vknzxsy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_success_pgwb7aopw = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_success_pgwb7aopw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_contextmenuRemove_m0hdwr84e = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_contextmenuRemove_m0hdwr84e.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_contextmenuRemove = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_contextmenuRemove.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_dvvl4if8n = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_dvvl4if8n.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_click_rjyb5j3fi = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_click_rjyb5j3fi.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_click = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_9004123373732307_success_0wh7g6rst = async (event, scope) => {
    return await actions.inst_evh_9004123373732307_success_0wh7g6rst.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_9004123373732307_click_jk0fhmzg6 = async (event, scope) => {
    return await actions.inst_evh_9004123373732307_click_jk0fhmzg6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_9004123373732307_click = async (event, scope) => {
    return await actions.inst_evh_9004123373732307_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="WasteBinReadings"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <VueSlickgrid
      name="wasteBinReadings"
      :headerTitle="gettext('Waste Bin Readings')"
      :items="ds"
      export-to-csv
      :exportToCsvFilename="pgettext('ExportFilename', 'waste-bin-readings')"
      checkbox-row-selection
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      :contextMenu="b_2248226175642056_contextMenu"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
      @contextmenu:remove="evh_2248226175642056_contextmenuRemove($event, {})"
      @command="evh_2248226175642056_command($event, {})"
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2">
          <div>
            <ItpFormCheckbox
              name="skip_archived"
              :label="pgettext('WasteBinReadings', 'Include handled readings')"
              inline
              v-model="dsParams.include_worksheets"
              :value="dsParams.include_worksheets"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (dsParams.include_worksheets =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <ItpButton
            icon="fa-cog"
            :text="gettext('Create Worksheets...')"
            size="sm"
            :disabled="!selectedItems || !selectedItems.length"
            variant="primary"
            @click="evh_2095089031469157_click($event, {})"
          >
          </ItpButton>
          <ItpButton
            icon="fa-trash"
            :text="gettext('Delete')"
            size="sm"
            variant="danger"
            :disabled="!selectedItems || !selectedItems.length"
            :spinning="actions.deleteItems.isRunning"
            @click="evh_9004123373732307_click($event, {})"
          >
          </ItpButton>
          <PageHelp path="/assets/fixed-assets.html"> </PageHelp>
        </div>
      </template>
    </VueSlickgrid>
  </ItpPage>
</template>
